import { Share2, Plus, ArrowLeft, Star } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Summary from "./Tabs/summary";
import FairValue from "./Tabs/fairValue";
import Earnings from "./Tabs/earnings";
import Financial from "./Tabs/financial";
import Valuation from "./Tabs/valuation";
import AddToWatchlistDialog from "./Widgets/AddToWatchlistDialog";
import OverflowTabs from "./Widgets/overflowTabs";
import Growth from "./Tabs/growth";
import Risk from "./Tabs/risk";
import BalanceSheet from "./Tabs/balanceSheet";
import Profitability from "./Tabs/profitability";
import Ratings from "./Tabs/ratings";
import Peers from "./Tabs/peers";
import Momentum from "./Tabs/momentum";
/* ── Earnings-note tabs (2026-08-10) — mirror the MU earnings-note layout ── */
import EarningsReview from "./Tabs/earningsReview";
import SectorContext from "./Tabs/sectorContext";
import Thesis from "./Tabs/thesis";
import ScreenCatalysts from "./Tabs/screenCatalysts";
/* ── Valuation tab (2026-08-10) — intrinsic value + FV builder + history ── */
import IntrinsicValue from "./Tabs/intrinsicValue";
/* ── Options tab (2026-08-18) — IV term structure + implied moves ── */
import ImpliedVolatility from "./Tabs/impliedVolatility";
import { ACTIVE_MODEL, MODEL_CONFIG } from "../../config/modelConfig";
import { useMarket } from "../../context/MarketContext";
import { formatCurrency } from "../../utils/currency";   // ← import util

const tabs = [
  "Summary",
  "Fair Value",
  /* sits next to Fair Value — same valuation story, different lens */
  "Intrinsic Value",
  "FA Ratings",
  "Financial",
  "Earnings",
  "Valuation",
  "Growth",
  "Momentum",
  MODEL_CONFIG[ACTIVE_MODEL].profitabilityTab,
  "Risk",
  /* 6th FA factor — split out of Risk (which is now price risk) */
  "Balance Sheet Quality",
  "Peers",
  /* ── Earnings-note tabs ── */
  "Earnings Review",
  "Sector Context",
  "Thesis",
  "Screen & Catalysts",
  /* ── Options ── */
  "Implied Volatility",
];

const TAB_COMPONENTS = {
  Summary, "Fair Value": FairValue, "FA Ratings": Ratings, Earnings, Financial, Valuation,
  Growth, Momentum, Risk, Peers,
  "Balance Sheet Quality": BalanceSheet,
  "Earnings Review": EarningsReview,
  "Sector Context": SectorContext,
  Thesis,
  "Screen & Catalysts": ScreenCatalysts,
  "Intrinsic Value": IntrinsicValue,
  "Implied Volatility": ImpliedVolatility,
  [MODEL_CONFIG[ACTIVE_MODEL].profitabilityTab]: Profitability,
};

const StockLayout = ({ stock, stockPriceChange, activeTab }) => {
  const { ticker } = useParams();
  const navigate = useNavigate();
  const { market } = useMarket();
  const priceChange = stockPriceChange;
  const hasQuoteData = stockPriceChange != null && (stockPriceChange.change != null || stockPriceChange.change_pct != null);
  const isPositive = hasQuoteData ? Number(priceChange.change ?? 0) >= 0 : true;

  // ── removed local formatCurrency — now using util from utils/currency.js ──

  const [openWatchlist, setOpenWatchlist] = useState(false);

  const TAB_ROUTES = {
    [MODEL_CONFIG[ACTIVE_MODEL].profitabilityTab]: "profitability",
    "FA Ratings": "ratings",
    "Fair Value": "fairvalue",
    "Balance Sheet Quality": "balancesheet",
    "Earnings Review": "earnings-review",
    "Sector Context": "sector-context",
    Thesis: "thesis",
    "Screen & Catalysts": "screen-catalysts",
    "Intrinsic Value": "intrinsic-value",
    "Implied Volatility": "implied-volatility",
  };

  const ROUTE_TO_TAB = Object.fromEntries(
    Object.entries(TAB_ROUTES).map(([label, slug]) => [slug, label])
  );

  const activeLabel = ROUTE_TO_TAB[activeTab?.toLowerCase()] ?? activeTab;

  const handleTabClick = (tab) => {
    const slug = TAB_ROUTES[tab] ?? tab.toLowerCase();
    navigate(`/stocks/${ticker}/details/${slug}`);
  };

  const ActiveComponent = TAB_COMPONENTS[activeLabel] || Summary;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--color-bg-primary)" }}>

      {/* ── TOP BAR ── */}
      <div
        className="flex items-center justify-between"
        style={{ padding: "var(--spacing-md) var(--spacing-md) var(--spacing-sm)" }}
      >
        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 font-medium group"
          style={{
            color: "var(--color-text-muted)",
            fontSize: "var(--font-size-sm)",
            transition: "var(--transition-fast)",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-text-primary)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-text-muted)")}
        >
          <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
          <span className="hidden sm:inline">Back</span>
        </button>

        {/* Actions */}
        <div className="flex items-center gap-1.5">
          <button
            className="flex items-center gap-1.5 px-3 py-2"
            style={{
              border: "1px solid var(--color-border-primary)",
              backgroundColor: "var(--color-surface)",
              color: "var(--color-text-secondary)",
              fontSize: "var(--font-size-sm)",
              fontWeight: "var(--font-weight-semibold)",
              transition: "var(--transition-fast)",
              borderRadius: "var(--radius-lg)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--color-surface-hover)";
              e.currentTarget.style.borderColor = "var(--color-border-secondary)";
              e.currentTarget.style.color = "var(--color-text-primary)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "var(--color-surface)";
              e.currentTarget.style.borderColor = "var(--color-border-primary)";
              e.currentTarget.style.color = "var(--color-text-secondary)";
            }}
            onClick={() => setOpenWatchlist(true)}
          >
            <Plus size={14} />
            <span className="hidden xs:inline sm:inline">Watchlist</span>
          </button>
        </div>
      </div>

      {/* ── HEADER ── */}
      <div style={{ padding: "var(--spacing-xs) var(--spacing-md) var(--spacing-md)" }}>
        <div className="flex items-start justify-between gap-4">

          {/* Left: Logo + Info */}
          <div className="flex items-start gap-3 min-w-0">

            {/* Logo */}
            {stock.logo_url ? (
              <div
                className="w-11 h-11 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center overflow-hidden flex-shrink-0"
                style={{
                  backgroundColor: "var(--color-surface-hover)",
                  border: "1px solid var(--color-border-secondary)",
                  borderRadius: "var(--radius-xl)",
                }}
              >
                <img
                  src={stock.logo_url}
                  alt={stock.ticker}
                  className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                />
              </div>
            ) : (
              <div
                className="w-11 h-11 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: "linear-gradient(to bottom right, var(--color-surface-hover), var(--color-surface))",
                  border: "1px solid var(--color-border-secondary)",
                  borderRadius: "var(--radius-xl)",
                }}
              >
                <span
                  style={{
                    color: "var(--color-primary)",
                    fontSize: "var(--font-size-lg)",
                    fontWeight: "var(--font-weight-bold)",
                  }}
                >
                  {stock.ticker?.charAt(0)}
                </span>
              </div>
            )}

            {/* Text block */}
            <div className="min-w-0">
              {/* Ticker + badges */}
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <h1
                  style={{
                    color: "var(--color-text-primary)",
                    fontSize: "var(--font-size-2xl)",
                    fontWeight: "var(--font-weight-black)",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.2,
                  }}
                >
                  {stock.ticker}
                </h1>

                {stock.exchange && (
                  <span
                    style={{
                      color: "var(--color-text-muted)",
                      backgroundColor: "var(--color-surface-hover)",
                      border: "1px solid var(--color-border-secondary)",
                      borderRadius: "var(--radius-sm)",
                      fontSize: "var(--font-size-xs2)",
                      fontWeight: "var(--font-weight-semibold)",
                      padding: "2px 8px",
                    }}
                  >
                    {stock.exchange}
                  </span>
                )}

                {stock.sector_name && (
                  <span
                    className="hidden sm:inline"
                    style={{
                      color: "#FFFFFF",
                      backgroundColor: "var(--color-primary)",
                      borderRadius: "var(--radius-sm)",
                      fontSize: "var(--font-size-xs2)",
                      fontWeight: "var(--font-weight-semibold)",
                      padding: "2px 8px",
                    }}
                  >
                    {stock.sector_name}
                  </span>
                )}
              </div>

              {/* Company name */}
              <p
                className="truncate"
                style={{
                  color: "var(--color-text-secondary)",
                  fontSize: "var(--font-size-sm)",
                  fontWeight: "var(--font-weight-medium)",
                  maxWidth: "28ch",
                }}
              >
                {stock.company_name}
              </p>

              {/* Price row — aria-live announces price changes to screen readers (3.2.2) */}
              <div className="mt-2 flex items-end flex-wrap gap-2" aria-live="polite" aria-atomic="true">
                <span
                  style={{
                    color: "var(--color-text-primary)",
                    fontSize: "var(--font-size-4xl)",
                    fontWeight: "var(--font-weight-black)",
                    letterSpacing: "-0.03em",
                    lineHeight: 1,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatCurrency(stock.current_price)}
                </span>

                {hasQuoteData ? (
                  <div
                    className="flex items-center gap-1 px-2.5 py-1"
                    style={{
                      backgroundColor: isPositive ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
                      color: isPositive ? "var(--color-success)" : "var(--color-error)",
                      border: isPositive ? "1px solid rgba(16,185,129,0.2)" : "1px solid rgba(239,68,68,0.2)",
                      borderRadius: "var(--radius-md)",
                      fontSize: "var(--font-size-sm)",
                      fontWeight: "var(--font-weight-bold)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    <span style={{ fontSize: "var(--font-size-xs2)" }}>
                      {isPositive ? "▲" : "▼"}
                    </span>
                    <span>
                      {isPositive ? "+" : ""}{Number(priceChange.change ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <span style={{ opacity: 0.6, fontSize: "var(--font-size-xs2)" }}>|</span>
                    <span>
                      {isPositive ? "+" : ""}{Number(priceChange.change_pct ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                    </span>
                  </div>
                ) : (
                  <div
                    className="flex items-center gap-1 px-2.5 py-1"
                    style={{
                      borderRadius: "var(--radius-md)",
                      fontSize: "var(--font-size-xs)",
                      fontWeight: "var(--font-weight-medium)",
                      color: "var(--color-text-muted)",
                      backgroundColor: "var(--color-surface-hover)",
                      border: "1px solid var(--color-border-primary)",
                    }}
                  >
                    Quote data unavailable
                  </div>
                )}
              </div>

              {/* Last updated */}
              {stockPriceChange?.date && (
                <div
                  className="mt-1.5 flex items-center gap-1.5 flex-wrap"
                  style={{
                    color: "var(--color-text-muted)",
                    fontSize: "var(--font-size-xs2)",
                    fontWeight: "var(--font-weight-medium)",
                  }}
                >
                  <span>
                    Updated:{" "}
                    {new Date(stockPriceChange.date).toLocaleString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </span>
                  <span style={{ opacity: 0.5 }}>•</span>
                  <span
                    style={{
                      color: stockPriceChange.market_open ? "var(--color-success)" : "var(--color-error)",
                      fontWeight: "var(--font-weight-semibold)",
                    }}
                  >
                    {stockPriceChange.market_open ? "Market Open" : "Market Closed"}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Right: Quick stats — visible lg+ only */}
          <div
            className="hidden lg:flex items-center gap-px rounded-2xl overflow-hidden flex-shrink-0"
            style={{
              backgroundColor: "var(--color-surface)",
              border: "1px solid var(--color-border-primary)",
              borderRadius: "var(--radius-xl)",
            }}
          >
            {[
              { label: "Open", value: stockPriceChange?.open },
              { label: "High", value: stockPriceChange?.high, color: "var(--color-success)" },
              { label: "Low", value: stockPriceChange?.low, color: "var(--color-error)" },
              { label: "Volume", value: stockPriceChange?.volume, isVolume: true },
            ].map((item, i) => (
                <div
                  key={i}
                  className="px-4 py-2.5 text-center border-r last:border-r-0"
                  style={{ borderRightColor: "var(--color-border-primary)" }}
                >
                  <div
                    className="uppercase tracking-wider mb-0.5"
                    style={{
                      fontSize: "var(--font-size-xs2)",
                      fontWeight: "var(--font-weight-semibold)",
                      color: "var(--color-text-muted)",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    className="tabular-nums"
                    style={{
                      fontSize: "var(--font-size-sm)",
                      fontWeight: "var(--font-weight-bold)",
                      color: item.color || "var(--color-text-primary)",
                    }}
                  >
                    {item.isVolume
                      ? item.value ? `${(item.value / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 })}M` : "—"
                      : item.value != null ? formatCurrency(item.value) : "—"}
                  </div>
                </div>
              ))}
            </div>
        </div>

        {/* Mini stats bar — visible on md and below */}
        <div
          className="flex lg:hidden mt-3 overflow-x-auto scrollbar-hide rounded-xl"
          style={{
            border: "1px solid var(--color-border-primary)",
            backgroundColor: "var(--color-surface)",
            borderRadius: "var(--radius-lg)",
          }}
        >
          {[
            { label: "Open", value: stockPriceChange?.open },
            { label: "High", value: stockPriceChange?.high, color: "var(--color-success)" },
            { label: "Low", value: stockPriceChange?.low, color: "var(--color-error)" },
            { label: "Volume", value: stockPriceChange?.volume, isVolume: true },
          ].map((item, i) => (
              <div
                key={i}
                className="flex-1 min-w-[72px] text-center py-2 border-r last:border-r-0"
                style={{ borderRightColor: "var(--color-border-primary)" }}
              >
                <div
                  className="uppercase tracking-wider"
                  style={{
                    fontSize: "var(--font-size-xs2)",
                    fontWeight: "var(--font-weight-semibold)",
                    color: "var(--color-text-muted)",
                  }}
                >
                  {item.label}
                </div>
                <div
                  className="tabular-nums"
                  style={{
                    fontSize: "var(--font-size-xs2)",
                    fontWeight: "var(--font-weight-bold)",
                    color: item.color || "var(--color-text-primary)",
                  }}
                >
                  {item.isVolume
                    ? item.value ? `${(item.value / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 })}M` : "—"
                    : item.value != null ? formatCurrency(item.value) : "—"}
                </div>
              </div>
          ))}
        </div>
      </div>

      {/* ── TABS ── */}
      {/* Renders what fits and collapses the rest into a "More" menu; the
          active tab is always pulled into view. See Widgets/overflowTabs. */}
      <div style={{ padding: "0 var(--spacing-md)" }}>
        <OverflowTabs tabs={tabs} activeLabel={activeLabel} onSelect={handleTabClick} />
      </div>

      {/* ── TAB CONTENT ── */}
      <div className="mt-4">
        <ActiveComponent stock={stock} stockPriceChange={stockPriceChange} />
      </div>

      <AddToWatchlistDialog
        open={openWatchlist}
        onClose={() => setOpenWatchlist(false)}
        ticker={stock.ticker}
      />
    </div>
  );
};

export default StockLayout;